/**
 * CORE-1108 — `livekit-client` (≈530 КБ) грузится, только когда комната или
 * эфир действительно идут через LiveKit.
 *
 * Expo кладёт модуль, который статически импортируют два ленивых экрана, в
 * общий чанк `__common`, а `__common` подключён к входной точке
 * (`@expo/metro-config` serializeChunks: `entryChunk.requiredChunks.add`). Так
 * LiveKit приезжал на каждую страницу, включая лендинг, хотя нужен только в
 * комнате и в эфире, и только когда бэкенд выбрал LiveKit, а не Agora.
 *
 * Поэтому на вебе классы берутся через `loadLiveKit()` (динамический импорт —
 * свой чанк), типы — `import type` (стираются при сборке), а строковые
 * значения enum-ов — отсюда. Значения дословно повторяют пакет; тест
 * `liveKitLazy-CORE1108` сверяет их с `livekit-client`, чтобы обновление
 * пакета не разошлось молча. Натив (`*.native.ts`) импортирует пакет как
 * раньше: там чанков нет.
 */
import type * as LiveKit from 'livekit-client';
import { safeImport } from '../../utils/staleBundle';

export type LiveKitModule = typeof LiveKit;

let loaded: LiveKitModule | null = null;
let pending: Promise<LiveKitModule> | null = null;

/** Загрузить пакет (один раз на приложение). Отказ не кэшируется: следующий вызов попробует снова. */
export function loadLiveKit(): Promise<LiveKitModule> {
  if (loaded) return Promise.resolve(loaded);
  if (!pending) {
    pending = safeImport(() => import('livekit-client'))
      .then((m) => { loaded = m; return m; })
      .catch((e) => { pending = null; throw e; });
  }
  return pending;
}

/**
 * Уже загруженный пакет — для кода, который зовут только после `loadLiveKit()`
 * (`useRoomRtc` включает LiveKit-хук, лишь дождавшись загрузки).
 */
export function liveKitLoaded(): LiveKitModule {
  if (!loaded) throw new Error('livekit-client is not loaded yet — await loadLiveKit() first');
  return loaded;
}

// Строковые enum-ы пакета. `as unknown as` — enum-член TypeScript не принимает
// строку напрямую, а сигнатуры `room.on(...)` / `getTrackPublication(...)`
// типизированы именно членами.
const ev = <K extends keyof typeof LiveKit.RoomEvent>(v: string) => v as unknown as (typeof LiveKit.RoomEvent)[K];
const kind = <K extends keyof typeof LiveKit.Track.Kind>(v: string) => v as unknown as (typeof LiveKit.Track.Kind)[K];
const source = <K extends keyof typeof LiveKit.Track.Source>(v: string) => v as unknown as (typeof LiveKit.Track.Source)[K];

export const LkRoomEvent = {
  Reconnected: ev<'Reconnected'>('reconnected'),
  Disconnected: ev<'Disconnected'>('disconnected'),
  ConnectionStateChanged: ev<'ConnectionStateChanged'>('connectionStateChanged'),
  ParticipantConnected: ev<'ParticipantConnected'>('participantConnected'),
  ParticipantDisconnected: ev<'ParticipantDisconnected'>('participantDisconnected'),
  TrackPublished: ev<'TrackPublished'>('trackPublished'),
  TrackSubscribed: ev<'TrackSubscribed'>('trackSubscribed'),
  TrackUnpublished: ev<'TrackUnpublished'>('trackUnpublished'),
  TrackUnsubscribed: ev<'TrackUnsubscribed'>('trackUnsubscribed'),
  TrackMuted: ev<'TrackMuted'>('trackMuted'),
  TrackUnmuted: ev<'TrackUnmuted'>('trackUnmuted'),
  ActiveSpeakersChanged: ev<'ActiveSpeakersChanged'>('activeSpeakersChanged'),
  // Не «audioPlaybackStatusChanged»: у пакета имя и значение расходятся.
  AudioPlaybackStatusChanged: ev<'AudioPlaybackStatusChanged'>('audioPlaybackChanged'),
} as const;

export const LkTrackKind = {
  Audio: kind<'Audio'>('audio'),
  Video: kind<'Video'>('video'),
} as const;

export const LkTrackSource = {
  Camera: source<'Camera'>('camera'),
  Microphone: source<'Microphone'>('microphone'),
  ScreenShare: source<'ScreenShare'>('screen_share'),
  ScreenShareAudio: source<'ScreenShareAudio'>('screen_share_audio'),
  Unknown: source<'Unknown'>('unknown'),
} as const;
